import loadable from '@loadable/component';
import React from 'react';
import { connect } from 'react-redux';

interface BannerProps {
    domain?: string;
    isSearch?: boolean;
    isAuthenticated?: boolean;
    handleTracking?: Function;
}

const CoronaVirusBanner = loadable(
    () => import('../../../../components/Banners'),
    {
        resolveComponent: (components: {
            CoronaVirusBanner: React.ReactElement;
        }) => components.CoronaVirusBanner,
    },
);
const VotingBanner = loadable(() => import('../../../../components/Banners'), {
    resolveComponent: (components: { VotingBanner: React.ReactElement }) =>
        components.VotingBanner,
});
const PlaybookBanner = loadable(
    () => import('../../../../components/Banners'),
    {
        resolveComponent: (components: {
            PlaybookBanner: React.ReactElement;
        }) => components.PlaybookBanner,
    },
);

// Logic for the special banners we put up sometimes
// The banner itself
const specialBanner = VotingBanner;

// The domains where the banner should be visible, leave the array as [''] for ALL Tlds
const specialBannerDomains = ['.com'];

// Checks if we are within the timeframe of the banner
const isSpecialBannerTimeFrame = () => {
    // To show the banner simultaneusly on many different TLDs at the EXACT same time, use UTC time like here https://github.com/eventbrite/eb-ui/pull/9301
    const today = new Date();

    // Edit these dates with the new ones once a new banner is requested, months are 0-indexed
    // Remember to put the day after the actual end of the banner
    const specialBannerDateStart = new Date(2021, 8, 24);
    const specialBannerDateEnd = new Date(2021, 8, 29);

    const startDate = today >= specialBannerDateStart;
    const endDate = today <= specialBannerDateEnd;
    return startDate && endDate;
};

export const verifyDomain = (domain: string) => {
    if (isSpecialBannerTimeFrame()) {
        let isSpecialDomain = false;

        specialBannerDomains.forEach((specialDomain) => {
            if (domain.endsWith(specialDomain)) {
                isSpecialDomain = true;
            }
        });

        return isSpecialDomain;
    } else {
        return (
            domain.endsWith('.com') ||
            domain.endsWith('.uk') ||
            domain.endsWith('.au')
        );
    }
};

const SEARCH_TRACKING_PARAM =
    'utm_source=eventbrite&utm_medium=banner&utm_campaign=search';
const HOME_TRACKING_PARAM =
    'utm_source=eventbrite&utm_medium=banner&utm_campaign=home';

const shouldBannerRender = (props: BannerProps) => {
    if (verifyDomain(props.domain || '')) {
        return true;
    }

    return false;
};

const getCategory = (isSearch: boolean, isAuthenticated: boolean) => {
    if (isSearch) {
        return 'search';
    }

    if (isAuthenticated) {
        return 'home-feed';
    }

    return 'home';
};

const getBanner = (isSearch: boolean) => {
    if (isSpecialBannerTimeFrame()) {
        return { Banner: isSearch ? null : specialBanner };
    }
    return { Banner: isSearch ? null : PlaybookBanner };
};

const USBanner = (props: BannerProps) => {
    const shouldRender = shouldBannerRender(props);

    if (!shouldRender) {
        return <CoronaVirusBanner />;
    } else {
        const category = getCategory(
            props.isSearch || false,
            props.isAuthenticated || false,
        );
        const trackingParams = props.isSearch
            ? SEARCH_TRACKING_PARAM
            : HOME_TRACKING_PARAM;

        const { Banner } = getBanner(props.isSearch || false);

        if (!Banner) {
            return null;
        }

        return (
            <Banner
                category={category}
                trackingParams={trackingParams}
                handleTracking={props?.handleTracking}
                domain={props.domain}
            />
        );
    }
};

const _mapStateToProps = (state: {
    app: Pick<BannerProps, 'domain'>;
    auth: Pick<BannerProps, 'isAuthenticated'>;
    user: Pick<BannerProps, 'isAuthenticated'>;
}) => ({
    domain: state.app.domain,
    isAuthenticated: state.auth
        ? state.auth.isAuthenticated
        : state.user.isAuthenticated,
});

const _mapDispatchToProps = (dispatch: Function) => ({
    handleTracking: (...args: any[]) => dispatch(...args),
});

export default connect(_mapStateToProps, _mapDispatchToProps)(USBanner);
